'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridDiagonalBottomProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridDiagonalBottom - Diagonal grid pattern with bottom fade
 *
 * 45-degree crossed lines creating a diamond/argyle pattern
 * that fades from the bottom.
 *
 * Fixed fadeSize: 0.50
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridDiagonalBottom />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridDiagonalBottom({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridDiagonalBottomProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.5
  const sizeX = `${fadeSize * 100}%`
  const sizeY = `${fadeSize * 100 * 0.85}%`
  const maskGradient = `radial-gradient(ellipse ${sizeX} ${sizeY} at 50% 100%, #000 45%, transparent 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          repeating-linear-gradient(45deg, color-mix(in srgb, var(--border), transparent 60%) 0px, color-mix(in srgb, var(--border), transparent 60%) 1px, transparent 1px, transparent 20px),
          repeating-linear-gradient(-45deg, color-mix(in srgb, var(--border), transparent 60%) 0px, color-mix(in srgb, var(--border), transparent 60%) 1px, transparent 1px, transparent 20px)
        `,
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
